import { createI18n } from 'vue-i18n'
import es from './locales/es'
import en from './locales/en'
import fr from './locales/fr'
import it from './locales/it'
import { readStore, writeStore } from '@/lib/storage'

/**
 * Idioma de la interfaz.
 *
 * Los cuatro son los mismos que ya se ofrecen en los subtítulos en vivo y en
 * las pistas .vtt de las grabaciones: quien sigue una clase en francés espera
 * que los botones también estén en francés.
 *
 * El español es el idioma fuente (las claves se escriben contra él) y el de
 * respaldo: una clave que falte en otro idioma sale en español en vez de
 * aparecer como el nombre de la clave.
 */
export const SUPPORTED_LOCALES = [
  { code: 'es', label: 'Español' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'it', label: 'Italiano' },
] as const

export type LocaleCode = (typeof SUPPORTED_LOCALES)[number]['code']

const DEFAULT_LOCALE: LocaleCode = 'es'

function isSupported(code: string | null | undefined): code is LocaleCode {
  return !!code && SUPPORTED_LOCALES.some((l) => l.code === code)
}

function stored(): LocaleCode | null {
  const value = readStore('uiLanguage')
  return isSupported(value) ? value : null
}

/**
 * Qué idioma se abre, por orden:
 *
 *  1. Lo que esta persona eligió aquí alguna vez. Una elección explícita manda
 *     sobre todo lo demás, también sobre lo que diga Moodle: si alguien con el
 *     campus en catalán prefiere leer la sala en inglés, es asunto suyo.
 *  2. `?lang=` — Moodle lo añade al abrir la sala, así que la primera vez el
 *     aula sale en el idioma del campus sin que nadie toque nada.
 *  3. El idioma del navegador, para quien entra por un enlace suelto.
 *  4. Español.
 */
function detect(): LocaleCode {
  const saved = stored()
  if (saved) return saved

  try {
    const fromUrl = new URLSearchParams(window.location.search).get('lang')
    // Moodle habla en códigos como «es_mx» o «en_us»: nos quedamos con la raíz.
    const root = fromUrl ? fromUrl.replace('_', '-').split('-')[0].toLowerCase() : null
    if (isSupported(root)) return root
  } catch {
    /* sin URL utilizable; seguimos bajando */
  }

  const fromBrowser = (navigator.language || '').split('-')[0].toLowerCase()
  return isSupported(fromBrowser) ? fromBrowser : DEFAULT_LOCALE
}

export const i18n = createI18n({
  legacy: false,
  // `$t` disponible en cualquier plantilla sin importar nada en cada componente.
  globalInjection: true,
  locale: detect(),
  fallbackLocale: DEFAULT_LOCALE,
  // Sin avisos por consola al caer al respaldo: es el comportamiento buscado
  // mientras una traducción esté a medias, no un error.
  missingWarn: false,
  fallbackWarn: false,
  messages: { es, en, fr, it },
})

/**
 * Cómo se llama un idioma, en el propio idioma: «Français», no «FR» ni «Francés».
 *
 * Los nombres NO se traducen a propósito. Quien busca su idioma en una lista lo
 * busca escrito como lo escribe él, que es lo único que sabe reconocer sin leer
 * el resto; y un código de dos letras no se lo dice a nadie que no lo sepa ya.
 *
 * @param code Código del idioma (`es`, `en`…).
 * @returns Su nombre, o el código en mayúsculas si es uno que no ofrecemos —
 *          que es lo que se veía antes, así que nunca queda peor que estaba.
 */
export function languageName(code: string): string {
  const known = SUPPORTED_LOCALES.find((l) => l.code === code)
  return known ? known.label : (code || '').toUpperCase()
}

// La preposición con la que cada idioma dice «en inglés», «in English»…
const IN_LANGUAGE: Record<string, string> = { es: 'en', en: 'in', fr: 'en', it: 'in' }

/**
 * La marca de una frase que se dijo en otro idioma y se lee traducida:
 * «(EN INGLÉS)», «(IN SPANISH)», «(EN ANGLAIS)», «(IN INGLESE)». La misma que
 * pone el backend encima de los subtítulos de las grabaciones (foreignLabel en
 * translation.handler), en el idioma que se lee.
 *
 * @param spoken El idioma en que se dijo.
 * @param reading El idioma en que se lee.
 * @returns null si es el mismo.
 */
export function foreignLabel(spoken: string, reading: string): string | null {
  const from = (spoken || '').split(/[-_]/)[0].toLowerCase()
  const to = (reading || '').split(/[-_]/)[0].toLowerCase()
  if (!from || !to || from === to) return null
  let name = from
  try {
    name = new Intl.DisplayNames([to], { type: 'language' }).of(from) || from
  } catch { /* sin datos de ese idioma: el código */ }
  return `(${IN_LANGUAGE[to] ?? 'in'} ${name})`.toLocaleUpperCase(to)
}

/** El idioma que se está leyendo ahora. */
export function currentLocale(): LocaleCode {
  return i18n.global.locale.value as LocaleCode
}

/**
 * El idioma que propone quien abre la sala (el campus, desde dentro del token).
 *
 * Es el paso 2 de `detect()`, pero llega tarde: el token no se puede leer aquí
 * —va cifrado— así que el idioma aparece cuando el servidor lo cuenta, ya
 * arrancada la aplicación. Se aplica solo si esta persona no ha elegido idioma
 * en este navegador, y NO se recuerda como elección suya: es el punto de
 * partida, no una decisión.
 */
export function applySuggestedLocale(code: string): void {
  if (stored()) return
  const root = (code || '').replace('_', '-').split('-')[0].toLowerCase()
  if (!isSupported(root)) return
  i18n.global.locale.value = root
  document.documentElement.setAttribute('lang', root)
}

/** Cambiarlo. Se recuerda en este navegador y se refleja en <html lang>. */
export function setLocale(code: LocaleCode): void {
  if (!isSupported(code)) return
  i18n.global.locale.value = code
  writeStore('uiLanguage', code)
  document.documentElement.setAttribute('lang', code)
}

/**
 * Traducir fuera de un componente (servicios, composables sin `setup`).
 * Dentro de un componente se usa `$t` en la plantilla o `useI18n()` en el
 * script, que son reactivos al cambio de idioma.
 */
export const t = i18n.global.t

document.documentElement.setAttribute('lang', currentLocale())

export default i18n
